import { gql, useLazyQuery } from "@apollo/client";
import type { ICustomerOrdersQuery, IOrdersEdges, IOrdersEdgesNode } from "../queries/customer-gql";
import { totalPaidAmountQuery } from "../queries/customer-gql";
import { useCallback, useEffect, useMemo, useState } from "react";

interface IProps {
  accessToken: string;
  startDate: Date;
  endDate: Date;
}

// TODO: Shopify에서 쿼리 키로 주문 필터 시 발생하는 에러가 해결되면, 해당 방식으로 주문을 필터링하여 조회할 예정
const useOrdersAmount = ({ accessToken, startDate, endDate }: IProps) => {
  // 모든 주문을 저장할 상태
  const [allOrders, setAllOrders] = useState<IOrdersEdges["edges"] | []>([]);
  // 전체 주문 데이터를 가져오는 중인지 여부
  const [isLoading, setIsLoading] = useState(false);

  // 전체 데이터를 가져오기 위한 쿼리
  const [fetchCustomerOrders] = useLazyQuery<ICustomerOrdersQuery>(gql(totalPaidAmountQuery.query), {
    fetchPolicy: "no-cache",
    onError: (error) => {
      setIsLoading(false);
      throw new Error(`[SHOPIFY][Customer][Orders] Failed to query : ${JSON.stringify(error)}`);
    },
  });

  // 모든 주문을 한 번에 가져오는 함수
  const getAllOrders = useCallback(async () => {
    try {
      setIsLoading(true);

      let hasMorePages = true;
      let paginationCursor = null;
      let allCollectedOrders: IOrdersEdges["edges"] = [];

      // 첫 요청은 최대 250개 가져오기
      const first = 250;

      while (hasMorePages) {
        // eslint-disable-next-line no-await-in-loop
        const { data: orderData } = await fetchCustomerOrders({
          variables: {
            customerAccessToken: accessToken,
            first,
            after: paginationCursor,
            sortKey: "PROCESSED_AT",
            reverse: true,
            // query: `processed_at:>=2024-04-01 AND processed_at:<=2024-12-23`,
          },
        });

        if (!orderData?.customer.orders) {
          break;
        }

        const currentOrders = orderData.customer.orders.edges || [];

        allCollectedOrders = [...allCollectedOrders, ...currentOrders];

        // 다음 페이지 확인
        hasMorePages = orderData.customer.orders.pageInfo.hasNextPage;
        paginationCursor = orderData.customer.orders.pageInfo.endCursor;
      }

      setAllOrders(allCollectedOrders);
    } catch (error) {
      throw new Error(`전체 주문 데이터 가져오기 실패: ${JSON.stringify(error)}`);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    void getAllOrders();
  }, []);

  // 기간내 실 주문금액 계산
  const totalOrderAmount = useMemo(() => {
    if (!allOrders.length) return 0;

    return allOrders
      .filter((edge: { node: IOrdersEdgesNode }) => {
        // 날짜 필터
        const processedAtDate = new Date(edge.node.processedAt);
        const isWithinDateRange = processedAtDate >= startDate && processedAtDate < endDate;

        // FinancialStatus 필터
        const isValidFinancialStatus =
          edge.node.financialStatus !== "PENDING" && edge.node.financialStatus !== "VOIDED";

        // 패밀리세일 주문 제외
        const isFamilySale = edge.node.isfamilysale?.value === "true";

        return isWithinDateRange && isValidFinancialStatus && !isFamilySale;
      })
      .reduce((total, item) => {
        const subTotalPrice = parseFloat(item.node.subtotalPrice?.amount ?? "0");
        const totalRefunded = parseFloat(item.node.totalRefunded.amount);
        const calculatedAmount = Math.round((subTotalPrice - totalRefunded) * 100) / 100;
        const accumulatedAmount = Math.max(Math.round(calculatedAmount * 100) / 100, 0);

        return total + accumulatedAmount;
      }, 0);
  }, [allOrders, startDate, endDate]);

  return {
    getAllOrders,
    totalOrderAmount,
    isOrdersLoading: isLoading,
  };
};

export default useOrdersAmount;
